/**
 * Standalone brand assets — string builders for the committed SVG files in
 * `assets/brand/`. The files are GENERATED from these builders (which share
 * `geometry.ts` with the element) so the letterform can never drift between
 * component and asset; a parity test asserts the committed files match.
 *
 * Regenerate after touching geometry:
 *
 *   bun -e 'import { writeBrandAssets } from "./src/brand/assets";
 *           await writeBrandAssets();'   # from packages/web-kit
 *
 * PNG favicon sizes are NOT generated here (no rasterizer dependency).
 * When needed, render `favicon.svg` at 16/32/48/180/192/512 with any
 * rasterizer, e.g.:  `rsvg-convert -w 32 -h 32 favicon.svg > favicon-32.png`
 * (or resvg/sharp). The 16px case is the design constraint the favicon
 * geometry is built for — verify that size first.
 */

import { STEM_PATH, BOWL_PATH, DOT, VIEW, dotCx } from "./geometry";

/** Brand inks (mirrors the CSS defaults in `styles.ts`). */
export const BRAND_INK_CREAM = "#f2ede3";
export const BRAND_INK_CHARCOAL = "#211d18";
export const BRAND_DOT_EMBER = "#de8d51";

export interface LogoSvgOptions {
  /** Stroke/fill ink. Default cream (dark theme first). */
  ink?: string;
  /** Dot color. Default the ink (spaceless mark). */
  dot?: string;
}

/** The bare »P.« mark (no app name) as a standalone SVG document. */
export function logoSvg(options: LogoSvgOptions = {}): string {
  const ink = options.ink ?? BRAND_INK_CREAM;
  const dot = options.dot ?? ink;
  const width = VIEW.minWidth;
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${VIEW.height}" role="img" aria-label="P.">
  <path id="p-stem" fill="${ink}" d="${STEM_PATH}"/>
  <path id="p-bowl" fill="${ink}" d="${BOWL_PATH}"/>
  <circle id="p-dot" fill="${dot}" cx="${dotCx(0)}" cy="${DOT.cy}" r="${DOT.r}"/>
</svg>
`;
}

/**
 * The favicon — the ratified tile variant (concept sheet 1, bottom-right):
 * the DARK two-stroke stencil p on the CREAM rounded-square tile — the
 * inverse of the on-dark mark, with NO dot. Pixel-measured from the
 * reference tile:
 *
 *   - stem: the ACTUAL v2 `STEM_PATH`, placed by transform; the favicon cut
 *     carries ~12 % extra horizontal weight (sx > sy). Glyph height ≈ 54 %
 *     of the tile.
 *   - bowl: a favicon-specific cut of the sail (`FAVICON_BOWL`, below) —
 *     the app-name gap collapses to a tight stencil slit (≈ 1.3 stem
 *     widths), the top hook is subtler, and the bottom tail sweeps further
 *     LEFT-down than the display bowl.
 *   - tile corner radius ≈ 10.4/64.
 *
 * The slit and the bowl's chord are the counters that must survive 16 px.
 */

/**
 * The favicon bowl — same anatomy as `BOWL_PATH` (solid sail: outer arc,
 * a vertical chord, tips hooking LEFT), re-cut for the tile as a
 * near-symmetric comma-sail: both tips reach x ≈ 33, stay ~2.5 units
 * thick to the end and cut BLUNT (no hairline points — they must survive
 * 16 px). Row-profile-measured off the reference then shifted LEFT by 2.72
 * units so the stem→closing-arc slit tightens to 70% of its first cut
 * (≈ 9.06 → 6.34 units), matching the display mark's offset reduction:
 * chord x 35.98, belly x 41.98 at y 26.5, tips at y 16 / 36.6.
 */
const FAVICON_BOWL = [
  "M 32.88 15.9", // top tip, left corner — hooks up-LEFT like the display bowl
  "C 33.88 15.7 34.78 15.8 35.38 16.1", // blunt top cut (softly rounded)
  "C 38.28 17.4 40.58 19.9 41.38 23", // outer edge: shoulder out and down
  "C 42.13 26 41.88 28.5 41.43 30.8", // around the belly (max ≈ 41.98 at y 26.5)
  "C 41.08 31.6 40.68 32.4 40.08 33.2", // easing into the tail
  "C 38.78 34.6 37.28 35.8 35.58 36.6", // the tail sweeps down-LEFT, still thick
  "C 34.68 37 33.78 36.9 33.08 36.6", // blunt bottom cut
  "C 34.18 36.1 35.38 35.2 35.78 34.4", // inner edge rises back right
  "C 35.93 34 35.98 33.8 35.98 33.4", // …meeting the chord bottom
  "L 35.98 20.2", // the chord — straight, vertical
  "C 35.88 18.5 34.48 17 32.88 15.9", // chord top curls out to the tip
  "Z",
].join(" ");

export type FaviconTheme = "light" | "dark";

/**
 * Favicon internals shared by the fixed-theme and adaptive builders. The
 * »P.« dot joined the tile (operator revision 2026-07-23): it sits on the
 * glyph's bottom line, right of the bowl — oversized relative to the display
 * mark so it survives 16 px (r 4 → a 2 px dot at 16 px). The composition
 * (stem + bowl + dot) is nudged 3.5 units LEFT so the widened glyph stays
 * optically centered in the tile.
 */
const FAVICON_STEM = { sx: 0.278, sy: 0.2486, tx: 13.2, ty: 11.72 };
const FAVICON_DOT = { cx: 48.5, cy: 46.5, r: 4 };
const FAVICON_SHIFT = -3.5;

const faviconBody = (ink: string, dot: string): string =>
  `  <g transform="translate(${FAVICON_SHIFT} 0)">
    <path id="p-stem" fill="${ink}" transform="translate(${FAVICON_STEM.tx} ${FAVICON_STEM.ty}) scale(${FAVICON_STEM.sx} ${FAVICON_STEM.sy})" d="${STEM_PATH}"/>
    <path id="p-bowl" fill="${ink}" d="${FAVICON_BOWL}"/>
    <circle id="p-dot" fill="${dot}" cx="${FAVICON_DOT.cx}" cy="${FAVICON_DOT.cy}" r="${FAVICON_DOT.r}"/>
  </g>`;

/** A fixed-theme favicon: cream tile / charcoal glyph (light) or the inverse (dark); ember dot in both. */
export function faviconSvg(theme: FaviconTheme = "light"): string {
  const tile = theme === "light" ? BRAND_INK_CREAM : BRAND_INK_CHARCOAL;
  const ink = theme === "light" ? BRAND_INK_CHARCOAL : BRAND_INK_CREAM;
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" role="img" aria-label="P">
  <rect id="tile" width="64" height="64" rx="10.4" fill="${tile}"/>
${faviconBody(ink, BRAND_DOT_EMBER)}
</svg>
`;
}

/**
 * The adaptive favicon — light by default, dark under
 * `prefers-color-scheme: dark` (SVG favicons evaluate internal media
 * queries in modern browsers; the fixed-theme files exist for consumers
 * that need explicit variants).
 */
export function faviconAdaptiveSvg(): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" role="img" aria-label="P">
  <style>
    #tile { fill: ${BRAND_INK_CREAM}; }
    #p-stem, #p-bowl { fill: ${BRAND_INK_CHARCOAL}; }
    @media (prefers-color-scheme: dark) {
      #tile { fill: ${BRAND_INK_CHARCOAL}; }
      #p-stem, #p-bowl { fill: ${BRAND_INK_CREAM}; }
    }
  </style>
  <rect id="tile" width="64" height="64" rx="10.4"/>
  <g transform="translate(${FAVICON_SHIFT} 0)">
    <path id="p-stem" transform="translate(${FAVICON_STEM.tx} ${FAVICON_STEM.ty}) scale(${FAVICON_STEM.sx} ${FAVICON_STEM.sy})" d="${STEM_PATH}"/>
    <path id="p-bowl" d="${FAVICON_BOWL}"/>
    <circle id="p-dot" fill="${BRAND_DOT_EMBER}" cx="${FAVICON_DOT.cx}" cy="${FAVICON_DOT.cy}" r="${FAVICON_DOT.r}"/>
  </g>
</svg>
`;
}

/** Write the committed asset files (run from `packages/web-kit`). */
export async function writeBrandAssets(
  dir = `${import.meta.dir}/../../assets/brand`
): Promise<void> {
  await Bun.write(`${dir}/logo.svg`, logoSvg());
  await Bun.write(`${dir}/favicon.svg`, faviconAdaptiveSvg());
  await Bun.write(`${dir}/favicon-light.svg`, faviconSvg("light"));
  await Bun.write(`${dir}/favicon-dark.svg`, faviconSvg("dark"));
}
